/* ============================================================
   Wido Ops — company hours report (HR/BOD, perm "payroll.report").
   The 7 paid-time metrics per employee, ordered by start date,
   month/year selectable, one-click export to Google Sheets.
   Two views:
     • Daily detail — per day, two columns: Paid hours (8h - unpaid) and
       Annual leave / PTO hours taken that day (from the backend `byDay`).
     • Summary — just the 7 monthly totals.
   Reads the AUTHORITATIVE backend report (GET /attendance/hours-report) so the
   numbers match the dashboard Hours summary; Export triggers the server-side
   write into the configured sheet.
   Exports window.HoursReportScreen.
   ============================================================ */
(function () {
  const { useState, useEffect } = React;
  const D = window.WidoData;
  const PersonCell = window.HRPersonCell;
  const MN = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

  function h1(min) { return Math.round(((min || 0) / 60) * 10) / 10; }
  function fmtH(min) { return h1(min).toFixed(1); }
  function fmtD(d) { return (Math.round((d || 0) * 10) / 10).toString(); }
  function cellH(min) { return min ? fmtH(min) : "·"; }
  // PAID and PTO are mutually exclusive per day: byDay.paidMin includes the PTO
  // portion (paid leave is paid), so carve it out — full leave shows PTO only,
  // half leave shows 4h paid + 4h PTO.
  function paidExclPto(d) { return Math.max(0, (d.paidMin || 0) - (d.ptoMin || 0)); }

  // Each summary column reads a backend row field directly (no client recompute).
  const COLS = [
    { key: "standard", label: "Standard hours", get: (r) => fmtH(r.standardMin), sub: (r) => "(" + r.standardDays + "d)" },
    { key: "actual", label: "Actual work hours", get: (r) => fmtH(r.actualWorkMin) },
    { key: "overtime", label: "Overtime hours", get: (r) => fmtH(r.overtimeMin) },
    { key: "paidleave", label: "Paid leave hours", get: (r) => fmtH(r.paidLeaveMin), sub: (r) => r.leaveDays ? "(" + fmtD(r.leaveDays) + "d)" : "" },
    { key: "unpaid", label: "Unpaid hours", get: (r) => fmtH(r.unpaidMin), danger: (r) => r.unpaidMin > 0 },
    { key: "paid", label: "Paid hours", get: (r) => fmtH(r.paidMin), sub: (r) => "(" + fmtD(r.paidDays) + "d)", strong: true },
    { key: "lunch", label: "Lunch days", get: (r) => fmtD(r.lunchDays) },
  ];

  function personSub(p) { return p.title ? undefined : window.rawText(p.teamName || D.helpers.teamName(p.team)); }

  // Per-(year,month) request de-dupe. The app shell remounts every screen once on
  // page load (app.js bumps `dataVersion` in the ErrorBoundary key when the
  // background bootstrap finishes), which would otherwise fire a second identical
  // GET. Reuse an in-flight request, or a just-resolved result within a short TTL,
  // so the remount doesn't duplicate the call — while a real month/year change
  // (different key) still fetches fresh.
  const reportCache = {};      // "year-month" -> { promise } | { rows, at }
  const REPORT_TTL = 4000;     // ms — spans the remount, short enough to stay fresh
  function fetchReport(year, month) {
    const key = year + "-" + month;
    const hit = reportCache[key];
    if (hit) {
      if (hit.promise) return hit.promise;                               // in-flight -> share it
      if (Date.now() - hit.at < REPORT_TTL) return Promise.resolve(hit.rows);  // fresh -> reuse
    }
    const promise = window.WidoApi.hoursReport(year, month)
      .then((res) => { const rows = (res && res.rows) || []; reportCache[key] = { rows, at: Date.now() }; return rows; })
      .catch((e) => { delete reportCache[key]; throw e; });
    reportCache[key] = { promise };
    return promise;
  }

  function HoursReportScreen({ settings, onToast }) {
    const [year, setYear] = useState((D && D.CUR_YEAR) || 2026);
    const [month, setMonth] = useState((D && D.CUR_MONTH) || 6);
    const [q, setQ] = useState("");
    const [view, setView] = useState("daily");          // daily | summary
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [busy, setBusy] = useState(false);
    const [confirmFull, setConfirmFull] = useState(false);   // full-grid export confirmation

    useEffect(() => {
      let live = true;
      setLoading(true);
      fetchReport(year, month)
        .then((rows) => { if (live) { setRows(rows); setLoading(false); } })
        .catch(() => { if (live) { setRows([]); setLoading(false); onToast && onToast(window.t("Couldn't load the hours report")); } });
      return () => { live = false; };
    }, [year, month]);

    const sys = (settings && settings.system) || (D.DEFAULT_SETTINGS && D.DEFAULT_SETTINGS.system) || {};
    const shown = rows.filter((r) => !q || r.person.name.toLowerCase().includes(q.toLowerCase()));

    // Two export modes, because they touch very different parts of the sheet:
    //   full (DEFAULT) — every day's Paid/PTO cells AND the OT + Lunch columns.
    //                    It replaces whatever is in the day grid, so it asks first.
    //   metricsOnly    — the OT + Lunch summary columns alone, for months whose
    //                    day grid is being kept by hand.
    async function exportSheet(metricsOnly) {
      setBusy(true);
      try {
        const res = await window.WidoApi.exportHoursReport(year, month, { metricsOnly });
        let msg = window.t("Exported to") + " " + (sys.sheetName || window.t("Google Sheet"))
          + " · " + window.t("tab") + " " + (res && res.tab ? res.tab : "")
          + " · " + window.t(metricsOnly ? "OT + Lunch only" : "days + OT + Lunch")
          + (res && typeof res.matched === "number" ? " · " + res.matched + " " + window.t("employees") : "");
        // Heads-up when some employees have no matching email row in the sheet.
        if (res && res.notInSheet && res.notInSheet.length) {
          msg += " · " + res.notInSheet.length + " " + window.t("not in sheet");
        }
        onToast && onToast(msg);
      } catch (e) {
        let msg = "";
        try { const j = JSON.parse(e.detail || "{}"); msg = j.detail || j.error || ""; } catch (_) {}
        onToast && onToast(msg || window.t("Export failed"));
      } finally { setBusy(false); setConfirmFull(false); }
    }

    // The default export replaces the month's day cells in a shared spreadsheet,
    // so it confirms once and names exactly what it will overwrite.
    function ConfirmFullExport() {
      return React.createElement("div", { className: "scrim-bg", onClick: () => setConfirmFull(false) },
        React.createElement("div", { className: "sheet", style: { maxWidth: 460 }, onClick: (e) => e.stopPropagation() },
          React.createElement("div", { className: "sheet__head" },
            React.createElement("h3", { className: "section-title", style: { fontSize: 20 } }, window.t("Export to Google Sheets?")),
            React.createElement("button", { className: "icon-btn", onClick: () => setConfirmFull(false) },
              React.createElement("span", { className: "material-symbols-rounded" }, "close"))),
          React.createElement("div", { className: "sheet__body" },
            React.createElement("div", { className: "banner", style: { background: "var(--md-error-container)", color: "var(--md-on-error-container)" } },
              React.createElement("span", { className: "material-symbols-rounded" }, "warning"),
              React.createElement("span", { style: { fontSize: 13, lineHeight: 1.5 } },
                window.t("This writes the daily hours, OT and Lunch for") + " ",
                React.createElement("strong", null, window.t(MN[month - 1]) + " " + year),
                " " + window.t("into") + " ",
                React.createElement("strong", null, sys.sheetName || window.t("the Google Sheet")),
                ". " + window.t("Anything typed into those day cells by hand is replaced."))),
            React.createElement("p", { className: "muted", style: { fontSize: 13, lineHeight: 1.6, margin: "14px 0 0" } },
              window.t("Use \"OT + Lunch only\" instead to leave the day cells alone."))),
          React.createElement("div", { className: "sheet__foot" },
            React.createElement("button", { className: "btn btn--text", onClick: () => setConfirmFull(false) }, window.t("Cancel")),
            React.createElement("button", { className: "btn btn--filled", disabled: busy, onClick: () => exportSheet(false) },
              React.createElement("span", { className: "material-symbols-rounded" }, "table_view"),
              window.t("Export")))));
    }

    // ---- Summary table (the 7 monthly totals) ----
    function SummaryTable() {
      return React.createElement("div", { className: "card tablewrap" },
        React.createElement("table", { className: "data" },
          React.createElement("thead", null, React.createElement("tr", null,
            [React.createElement("th", { key: "emp" }, window.t("Employee")), React.createElement("th", { key: "sd" }, window.t("Start date"))]
              .concat(COLS.map((c) => React.createElement("th", { key: c.key, style: { textAlign: "right", whiteSpace: "nowrap" } }, window.t(c.label)))))),
          React.createElement("tbody", null, shown.map((r) => React.createElement("tr", { key: r.person.id },
            React.createElement("td", null, React.createElement(PersonCell, { person: r.person, sub: personSub(r.person) })),
            React.createElement("td", { className: "muted mono", style: { fontSize: 12, whiteSpace: "nowrap" } }, r.startDate ? D.helpers.fmtDate(r.startDate) : "—"),
            COLS.map((c) => React.createElement("td", { key: c.key, className: "num", style: { color: (c.danger && c.danger(r)) ? "var(--md-error)" : (c.strong ? "var(--md-primary)" : undefined), fontWeight: c.strong ? 700 : undefined } },
              c.get(r), c.sub && c.sub(r) ? React.createElement("span", { className: "muted", style: { fontSize: 11, marginLeft: 3 } }, c.sub(r)) : null)))))));
    }

    // ---- Daily-detail table: ALL days of the month, weekends + holidays tinted,
    // each day = Paid + PTO columns, then the monthly totals. ----
    const WK_BG = "#2a6fdb22", WK_HEAD = "#2a6fdb33", HOL_BG = "#D6A82022", HOL_HEAD = "#D6A82033";
    function dowShort(d) { return ["S", "M", "T", "W", "T", "F", "S"][new Date(year, month - 1, d).getDay()]; }
    function colBg(d, head) { return d.holiday ? (head ? HOL_HEAD : HOL_BG) : (d.weekend ? (head ? WK_HEAD : WK_BG) : undefined); }
    function Legend(swatch, label, border) {
      return React.createElement("span", { className: "row", style: { gap: 6, fontSize: 12.5 } },
        React.createElement("span", { style: { width: 12, height: 12, borderRadius: 3, background: swatch, border: border ? "1px solid " + border : undefined } }), window.t(label));
    }
    function DailyTable() {
      const dayList = shown.length ? (shown[0].byDay || []) : [];   // every calendar day
      return React.createElement("div", { className: "stack-v", style: { gap: 10 } },
        React.createElement("div", { className: "row", style: { gap: 16, flexWrap: "wrap" } },
          Legend(WK_HEAD, "Weekend", "#2a6fdb55"),
          Legend(HOL_HEAD, "Holiday", "#D6A82066"),
          Legend("var(--md-primary)", "Paid"),
          Legend("#D6341E", "Annual leave (PTO)")),
        React.createElement("div", { className: "card tablewrap" },
          React.createElement("table", { className: "data", style: { fontSize: 13 } },
            React.createElement("thead", null,
              React.createElement("tr", null,
                [React.createElement("th", { key: "emp", rowSpan: 2, style: { position: "sticky", left: 0, background: "var(--md-surface-container-low)", zIndex: 2, minWidth: 150 } }, window.t("Employee"))]
                  .concat(dayList.map((d) => React.createElement("th", { key: "d" + d.day, colSpan: 2, style: { textAlign: "center", whiteSpace: "nowrap", borderLeft: "1px solid var(--md-outline-variant)", background: colBg(d, true), color: d.holiday ? "#8a6a00" : (d.weekend ? "#1f4f9e" : undefined) } },
                    React.createElement("div", { style: { display: "flex", flexDirection: "column", lineHeight: 1.15 } },
                      React.createElement("span", null, String(d.day).padStart(2, "0")),
                      React.createElement("span", { style: { fontSize: 9, opacity: 0.7, fontWeight: 400 } }, dowShort(d.day))))))
                  .concat(COLS.map((c) => React.createElement("th", { key: c.key, rowSpan: 2, style: { textAlign: "right", whiteSpace: "nowrap", borderLeft: "2px solid var(--md-outline)", verticalAlign: "bottom" } }, window.t(c.label))))),
              React.createElement("tr", null,
                dayList.map((d) => [
                  React.createElement("th", { key: "p" + d.day, title: window.t("Paid hours"), style: { textAlign: "right", fontSize: 11, color: "var(--md-primary)", borderLeft: "1px solid var(--md-outline-variant)", background: colBg(d, true) } }, window.t("Paid")),
                  React.createElement("th", { key: "l" + d.day, title: window.t("Annual leave (PTO)"), style: { textAlign: "right", fontSize: 11, color: "#D6341E", background: colBg(d, true) } }, window.t("PTO")),
                ]))),
            React.createElement("tbody", null, shown.map((r) => {
              const bd = {}; (r.byDay || []).forEach((d) => { bd[d.day] = d; });
              return React.createElement("tr", { key: r.person.id },
                React.createElement("td", { style: { position: "sticky", left: 0, background: "var(--md-surface)", zIndex: 1 } }, React.createElement(PersonCell, { person: r.person, sub: personSub(r.person) })),
                dayList.map((dd) => { const d = bd[dd.day] || {}; const bg = colBg(dd, false); return [
                  React.createElement("td", { key: "p" + dd.day, className: "num mono", style: { fontSize: 12, borderLeft: "1px solid var(--md-outline-variant)", background: bg, color: paidExclPto(d) ? "var(--md-on-surface)" : "var(--md-outline)" } }, cellH(paidExclPto(d))),
                  React.createElement("td", { key: "l" + dd.day, className: "num mono", style: { fontSize: 12, background: bg, color: d.ptoMin ? "#D6341E" : "var(--md-outline)" } }, cellH(d.ptoMin)),
                ]; })
                  .concat(COLS.map((c) => React.createElement("td", { key: c.key, className: "num", style: { borderLeft: "2px solid var(--md-outline)", color: (c.danger && c.danger(r)) ? "var(--md-error)" : (c.strong ? "var(--md-primary)" : undefined), fontWeight: c.strong ? 700 : undefined } },
                    c.get(r), c.sub && c.sub(r) ? React.createElement("span", { className: "muted", style: { fontSize: 11, marginLeft: 3 } }, c.sub(r)) : null))));
            })))));
    }

    return React.createElement("div", { className: "stack-v", style: { gap: 20 } },
      React.createElement("div", { className: "between wrap" },
        React.createElement("div", null,
          React.createElement("h2", { className: "section-title" }, window.t("Hours report")),
          React.createElement("p", { className: "muted", style: { margin: "4px 0 0" } }, window.t("Monthly paid-time summary for everyone, ordered by start date."))),
        React.createElement("div", { className: "row", style: { gap: 10, flexWrap: "wrap" } },
          React.createElement("div", { className: "seg" },
            React.createElement("button", { className: "seg__btn" + (view === "daily" ? " is-on" : ""), onClick: () => setView("daily") }, window.t("Daily detail")),
            React.createElement("button", { className: "seg__btn" + (view === "summary" ? " is-on" : ""), onClick: () => setView("summary") }, window.t("Summary"))),
          React.createElement("input", { className: "input", style: { height: 40, width: 180 }, placeholder: window.t("Search name…"), value: q, onChange: (e) => setQ(e.target.value) }),
          React.createElement("select", { className: "select", style: { height: 40 }, value: month, onChange: (e) => setMonth(+e.target.value) },
            MN.map((mn, i) => React.createElement("option", { key: mn, value: i + 1 }, window.t(mn)))),
          React.createElement("select", { className: "select", style: { height: 40 }, value: year, onChange: (e) => setYear(+e.target.value) },
            [2024, 2025, 2026].map((y) => React.createElement("option", { key: y, value: y }, y))),
          // Default = the complete export (days + OT + Lunch); it confirms first
          // because it replaces the month's day cells. Metrics-only is the opt-out.
          React.createElement("button", { className: "btn btn--filled", disabled: busy || loading, onClick: () => setConfirmFull(true), title: window.t("Writes every day's Paid/PTO hours plus the OT and Lunch columns.") },
            React.createElement("span", { className: "material-symbols-rounded" }, busy ? "hourglass_top" : "table_view"),
            window.t("Export to Google Sheets")),
          React.createElement("button", { className: "btn btn--tonal", disabled: busy || loading, onClick: () => exportSheet(true), title: window.t("Fills only the OT and Lunch summary columns, leaving the day cells untouched.") },
            React.createElement("span", { className: "material-symbols-rounded" }, "functions"),
            window.t("OT + Lunch only")))),

      confirmFull && React.createElement(ConfirmFullExport, null),

      loading
        ? React.createElement("div", { className: "card empty" }, React.createElement("span", { className: "material-symbols-rounded" }, "hourglass_top"), React.createElement("p", null, window.t("Loading…")))
        : (view === "daily" ? React.createElement(DailyTable, null) : React.createElement(SummaryTable, null)),

      React.createElement("p", { className: "muted", style: { margin: 0, fontSize: 12.5, display: "flex", gap: 6, alignItems: "center", flexWrap: "wrap" } },
        React.createElement("span", { className: "material-symbols-rounded", style: { fontSize: 16 } }, "table_view"),
        sys.sheetUrl
          ? React.createElement("span", null, window.t("Exports to") + ": ", React.createElement("a", { href: sys.sheetUrl, target: "_blank", rel: "noopener", style: { color: "var(--md-primary)" } }, sys.sheetName || "Google Sheet"))
          : React.createElement("span", null, window.t("No export sheet configured — set one in System settings."))));
  }

  window.HoursReportScreen = HoursReportScreen;
})();
